import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GuideMarkdown from "./GuideMarkdown";

export default function AdminGameGuide() {
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    api.get("/game/how-to-play").then(({ data }) => { if (active) setCurrent(data); })
      .catch(() => { if (active) setError("Unable to load the current guide. Reopen this tab to retry."); });
    return () => { active = false; };
  }, []);
  const choose = async (event) => {
    const selected = event.target.files?.[0];
    setFile(null); setDraft(null); setError("");
    if (!selected) return;
    if (!/\.(md|markdown)$/i.test(selected.name) || selected.size > 128 * 1024) {
      setError("Choose a .md or .markdown file up to 128 KB."); event.target.value = ""; return;
    }
    try {
      const bytes = await selected.arrayBuffer();
      const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
      if (!text.trim() || /[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(text)) throw new Error();
      setDraft(text); setFile(selected);
    } catch { setError("Choose a nonempty UTF-8 Markdown text file."); event.target.value = ""; }
  };
  const publish = async () => {
    if (!file || busy) return;
    setBusy(true); setError("");
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.put("/admin/game/how-to-play", form);
      setCurrent(data); setDraft(null); setFile(null);
      toast.success("How to Play published. Users will see it when they open the page.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const download = () => {
    if (!current) return;
    const url = URL.createObjectURL(new Blob([current.markdown], { type: "text/markdown;charset=utf-8" }));
    const anchor = document.createElement("a"); anchor.href = url; anchor.download = "how-to-play.md"; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <section className="space-y-4">
    <h2 className="text-lg font-semibold">How to Play instructions</h2>
    <p className="text-sm text-slate-400">Download the current guide, edit it as Markdown, then upload and preview before publishing. Maximum 128 KB, UTF-8. Headings, lists, links and tables are supported. HTML and embedded images are disabled.</p>
    <p className="text-xs text-amber-200">Uploading a guide changes instructions only. The live tier table and game policy remain visible; payout rules come from the backend.</p>
    <button disabled={!current || busy} onClick={download} className="flex gap-2 items-center text-sm text-emerald-400 disabled:opacity-50"><Download size={16} /> Download current Markdown</button>
    <label className="block rounded-xl border border-dashed border-slate-600 p-4 text-sm"><span className="flex gap-2 items-center mb-2"><Upload size={16} /> Choose Markdown file</span><input aria-label="How to Play Markdown file" type="file" accept=".md,.markdown,text/markdown,text/plain" disabled={busy} onChange={choose} className="block max-w-full text-xs" /></label>
    {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
    {file && <div className="space-y-3"><p className="text-xs text-slate-400">Ready to publish: {file.name}</p><button disabled={busy} onClick={publish} className="rounded-lg bg-emerald-400 text-black px-4 py-3 font-semibold disabled:opacity-50">{busy ? "Publishing…" : "Publish instructions"}</button></div>}
    {(draft !== null || current) && <div className="rounded-xl border border-slate-700 p-4"><h3 className="font-semibold mb-4">{draft !== null ? "Upload preview" : "Published guide"}</h3><GuideMarkdown>{draft ?? current.markdown}</GuideMarkdown></div>}
  </section>;
}
